import { useEffect, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useWindow } from '@krp/client-runtime'
import { CASHIER_ANALYTICS_WINDOWS, SALES_POINTS_WINDOWS } from '@krp/resource-receipts-ui'
import { ActionInfo, StatusMessage, Tabs } from '@krp/ui-kit'

import { useMyAccess } from '../platform/useMyAccess'

/** Адрес таба «Источники»: вход раздела без него открывает «Магазины». */
const SOURCES_PATH = '/sales-statistics/sources'

const TABS = [
  { id: 'shops', label: 'Магазины' },
  { id: 'sources', label: 'Источники' },
  { id: 'cashiers', label: 'Кассиры' },
] as const

/** Право смотреть статистику продаж (RP-STAT-UNIFY): отдельное от управления кассирами; какие магазины и источники видно — по допуску роли к ресурсам. */
export const SALES_READ = 'platform:sales:read'

/** Табы статистики открыты тем, у кого есть право смотреть продажи; сервер проверит его сам. */
export function SalesStatisticsGate({ children }: { children: ReactNode }) {
  const access = useMyAccess()
  return access.data !== undefined && !access.data.api.includes(SALES_READ)
    ? <StatusMessage kind="forbidden" title="Нет права смотреть статистику продаж" text="Статистика продаж доступна тем, кому выдано право просмотра продаж." />
    : <>{children}</>
}

/**
 * Три таба раздела «Статистика по продажам» (слово владельца 28.10.2026, 05.11.2026): «Магазины» — первым и по умолчанию, сводная статистика касс
 * разных источников; «Источники» — выбор базы или кабинета и его статистика; «Кассиры» — статистика кассира по всем его связям.
 * Таб — окно на своём адресе, переход заменяет окно.
 */
export function SalesStatisticsTabs({ selected }: { selected: 'sources' | 'shops' | 'cashiers' }) {
  const window = useWindow()
  const navigate = useNavigate()
  return (
    <ActionInfo id="receipts.sales-points.tabs" label="Магазины, источники и кассиры" layout="block">
      <Tabs
        label="Статистика по продажам"
        idPrefix="sales-statistics"
        items={TABS}
        selected={selected}
        onSelect={(id) => {
          if (id === selected) return
          if (id === 'shops') window.replaceWith(SALES_POINTS_WINDOWS.list, {})
          else if (id === 'cashiers') window.replaceWith(CASHIER_ANALYTICS_WINDOWS.statistics, {})
          else void navigate(SOURCES_PATH, { replace: true })
        }}
      />
    </ActionInfo>
  )
}

/**
 * Шапка входа раздела. Вход без выбора (`/sales-statistics`) открывает «Магазины», если человеку они доступны; таб «Источники» —
 * свой адрес, на нём вход остаётся выбором источника.
 */
export function SalesStatisticsSourcesTabs() {
  const window = useWindow()
  const { pathname } = useLocation()
  const access = useMyAccess()
  const shopsAllowed = access.data?.api.includes(SALES_READ) === true
  const chosen = pathname.endsWith(SOURCES_PATH)
  useEffect(() => {
    if (!chosen && shopsAllowed) window.replaceWith(SALES_POINTS_WINDOWS.list, {})
  }, [chosen, shopsAllowed, window])
  return <SalesStatisticsTabs selected="sources" />
}
